import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import {
  Button,
  Header,
  Card,
  Icon,
  Label,
  List,
  Form,
  Message,
  Divider,
  Radio,
} from "semantic-ui-react";
import { getAuthToken, getClientAud } from "../selectors";

const panelStyle = {
  background: "#fff",
  color: "#1a1a1a",
  borderRadius: 12,
  border: "1px solid rgba(0, 0, 0, 0.1)",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 8px 28px rgba(0, 0, 0, 0.06)",
  padding: "1.75rem 2rem 2rem",
  marginBottom: "2rem",
};

const paymentBoxStyle = {
  background: "#fafafa",
  borderRadius: 10,
  padding: "1.1rem 1.25rem 1.25rem",
  marginBottom: "1.5rem",
  border: "1px solid rgba(0, 0, 0, 0.08)",
};

const plans = [
  {
    key: "1m",
    months: 1,
    label: "1 Monat",
    price: 20,
    perMonth: 20,
    features: [
      "Voller Chat-Zugang für 1 Monat",
      "Automatische Verlängerung (kündbar)",
      "E-Mail-Support",
    ],
  },
  {
    key: "6m",
    months: 6,
    label: "6 Monate",
    price: 50,
    perMonth: 50 / 6,
    features: [
      "Voller Chat-Zugang für 6 Monate",
      "Spare gegenüber dem Monatsabo",
      "Priorisierter Support",
    ],
  },
  {
    key: "12m",
    months: 12,
    label: "12 Monate",
    price: 70,
    perMonth: 70 / 12,
    recommended: true,
    features: [
      "Voller Chat-Zugang für 12 Monate",
      "Bestes Preis‑Leistungs‑Verhältnis",
      "Priorisierter Support",
    ],
  },
];

function authHeaders(authToken, clientAud, json = false) {
  const h = {
    ...(authToken ? {
      Authorization: `Bearer ${authToken}`,
      JWT_AUD: clientAud,
    } : {}),
  };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

export default function SubscriptionPage() {
  const authToken = useSelector(getAuthToken);
  const clientAud = useSelector(getClientAud);
  const [stripe, setStripe] = useState(null);
  const [loadingKey, setLoadingKey] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);
  /** @type {"stripe_card" | "stripe_sepa" | "paypal"} */
  const [paymentMethod, setPaymentMethod] = useState("stripe_card");
  const [paypalAvailable, setPaypalAvailable] = useState(false);
  const [paypalPlans, setPaypalPlans] = useState(null);
  const [paypalSdkReady, setPaypalSdkReady] = useState(false);
  const [paypalReason, setPaypalReason] = useState(null);
  const mountedRef = React.useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const confirmPaypal = useCallback(async (subscriptionId, planKey) => {
    const res = await fetch("/api/v1/subscriptions/paypal_confirm", {
      method: "POST",
      headers: {
        ...authHeaders(authToken, clientAud, true),
      },
      credentials: "include",
      body: JSON.stringify({ subscription_id: subscriptionId, plan: planKey }),
    });
    if (res.status === 401) {
      window.location.href = "/login";
      return;
    }
    if (!res.ok) {
      let msg = "PayPal-Bestätigung fehlgeschlagen.";
      try {
        const j = await res.json();
        if (j.error === "email_mismatch") msg = "PayPal-E-Mail stimmt nicht mit deinem Konto überein.";
        else if (j.message) msg = j.message;
      } catch (_) { /* ignore */ }
      if (mountedRef.current) setError(msg);
      return;
    }
    window.location.href = "/chat";
  }, [authToken, clientAud]);

  useEffect(() => {
    async function checkPaypal() {
      try {
        const res = await fetch("/api/v1/subscriptions/paypal_offer", {
          headers: authHeaders(authToken, clientAud),
          credentials: "include",
        });
        if (res.status === 401) {
          window.location.href = "/login";
          return;
        }
        if (!res.ok) return;
        const j = await res.json();
        if (mountedRef.current) {
          setPaypalAvailable(!!j.enabled);
          if (!j.enabled && j.reason === "plans_missing") {
            setPaypalReason("missing_plans");
          }
        }
      } catch (_) { /* ignore */ }
    }
    checkPaypal();
  }, [authToken, clientAud]);

  useEffect(() => {
    if (paymentMethod !== "paypal" || !paypalAvailable) return;
    let cancelled = false;
    setPaypalSdkReady(false);
    setPaypalPlans(null);
    async function loadPaypalSdk() {
      const res = await fetch("/api/v1/subscriptions/paypal_offer", {
        headers: authHeaders(authToken, clientAud),
        credentials: "include",
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (!res.ok || cancelled) return;
      const j = await res.json();
      if (!j.enabled || !j.clientId || cancelled) return;
      if (mountedRef.current) setPaypalPlans(j.plans);
      const existing = document.getElementById("paypal-sdk-script");
      if (existing && window.paypal) {
        if (!cancelled && mountedRef.current) setPaypalSdkReady(true);
        return;
      }
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.id = "paypal-sdk-script";
      script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(j.clientId)}&vault=true&intent=subscription`;
      script.onload = () => {
        if (!cancelled && mountedRef.current) setPaypalSdkReady(true);
      };
      script.onerror = () => {
        if (mountedRef.current) setError("PayPal-Skript konnte nicht geladen werden.");
      };
      document.body.appendChild(script);
    }
    loadPaypalSdk();
    return () => { cancelled = true; };
  }, [paymentMethod, paypalAvailable, authToken, clientAud]);

  useEffect(() => {
    if (paymentMethod !== "paypal" || !paypalSdkReady || !window.paypal || !paypalPlans) return undefined;

    const instances = [];
    plans.forEach((p) => {
      const host = document.getElementById(`paypal-btn-${p.key}`);
      if (!host) return;
      host.innerHTML = "";
      const planId = paypalPlans[p.key];
      if (!planId) return;
      const buttons = window.paypal.Buttons({
        style: { layout: "vertical", label: "subscribe" },
        createSubscription(_data, actions) {
          return actions.subscription.create({ plan_id: planId });
        },
        onApprove(data) {
          return confirmPaypal(data.subscriptionID, p.key);
        },
        onError() {
          if (mountedRef.current) setError("PayPal-Fehler. Bitte erneut versuchen oder Stripe wählen.");
        },
      });
      if (buttons.isEligible()) {
        buttons.render(`#paypal-btn-${p.key}`);
        instances.push(buttons);
      }
    });

    return () => {
      instances.forEach((b) => {
        try { b.close(); } catch (_) { /* ignore */ }
      });
      plans.forEach((p) => {
        const el = document.getElementById(`paypal-btn-${p.key}`);
        if (el) el.innerHTML = "";
      });
    };
  }, [paymentMethod, paypalSdkReady, paypalPlans, confirmPaypal]);

  useEffect(() => {
    async function checkStatus() {
      const res = await fetch("/api/v1/subscriptions/status", {
        headers: authHeaders(authToken, clientAud),
        credentials: "include",
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (!res.ok) return;
      const json = await res.json();
      if (json.active) {
        window.location.replace("/chat");
      }
    }
    checkStatus();
  }, [authToken, clientAud]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    async function confirmSession() {
      const res = await fetch("/api/v1/subscriptions/confirm", {
        method: "POST",
        headers: {
          ...authHeaders(authToken, clientAud, true),
        },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (res.ok) {
        window.history.replaceState({}, document.title, "/subscribe");
        window.location.href = "/chat";
      } else if (mountedRef.current) {
        setError("Payment confirmation failed. Please contact support if already charged.");
      }
    }
    if (sessionId) { confirmSession(); }
  }, [authToken, clientAud]);

  useEffect(() => {
    async function loadStripe() {
      const res = await fetch("/api/v1/subscriptions/public_key", {
        headers: authHeaders(authToken, clientAud),
        credentials: "include",
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (!res.ok) {
        setError(`Failed to load Stripe key (${res.status})`);
        setLoadingKey(false);
        return;
      }
      const json = await res.json();
      if (!json.publishableKey) {
        setError("Stripe publishable key is not configured. Please set STRIPE_PUBLISHABLE_KEY.");
        setLoadingKey(false);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.onload = () => {
        const s = window.Stripe(json.publishableKey);
        setStripe(s);
      };
      document.body.appendChild(script);
      setLoadingKey(false);
    }
    setLoadingKey(true);
    loadStripe();
  }, [authToken, clientAud]);

  async function startCheckout(plan) {
    if (!stripe) return;
    setLoadingPlan(plan);
    const stripePayment = paymentMethod === "stripe_sepa" ? "sepa" : "card";
    const res = await fetch("/api/v1/subscriptions/checkout", {
      method: "POST",
      headers: {
        ...authHeaders(authToken, clientAud, true),
      },
      credentials: "include",
      body: JSON.stringify({ plan, stripe_payment: stripePayment }),
    });
    if (res.status === 401) {
      window.location.href = "/login";
      return;
    }
    if (!res.ok) {
      setError(`Checkout start failed (${res.status})`);
      setLoadingPlan(null);
      return;
    }
    const json = await res.json();
    if (json.id) await stripe.redirectToCheckout({ sessionId: json.id });
    setLoadingPlan(null);
  }

  return (
    <div className="container subscription-page" style={{ maxWidth: 960, margin: "0 auto" }}>
      <div style={panelStyle}>
        <Header as="h2" style={{ fontWeight: 600, marginBottom: 4, color: "#1a1a1a" }}>
          Abonnement wählen
        </Header>
        <p style={{ color: "#444", marginBottom: 8, fontSize: 15, lineHeight: 1.5 }}>
          Wähle zuerst die Zahlungsart, dann deinen Zeitraum. Karte, SEPA-Lastschrift und PayPal (falls aktiv) stehen zur Verfügung.
        </p>
        <p style={{ marginBottom: 22, fontSize: 14 }}>
          <NavLink to="/subscriptions" style={{ color: "#00847e", fontWeight: 500 }}>
            <Icon name="setting" />
            {" "}
            Bereits gebucht? Abo verwalten
          </NavLink>
        </p>

        {error && (
          <Message
            error
            onDismiss={() => setError(null)}
            style={{ marginBottom: "1.25rem" }}
            header="Das hat nicht geklappt"
            content={error}
          />
        )}

        <div style={paymentBoxStyle}>
          <div
            style={{
              fontSize: 12,
              color: "#888",
              textTransform: "uppercase",
              letterSpacing: 0.6,
              marginBottom: 12,
              fontWeight: 600,
            }}
          >
            Zahlungsmethode
          </div>
          <Form style={{ marginBottom: 0 }}>
            <Form.Field style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Icon name="credit card outline" size="large" color="teal" style={{ flexShrink: 0 }} />
                <Radio
                  label="Kreditkarte / Apple Pay (Stripe)"
                  name="subscription_pay"
                  value="stripe_card"
                  checked={paymentMethod === "stripe_card"}
                  onChange={(_e, { value }) => {
                    setPaymentMethod(value);
                    setError(null);
                  }}
                />
              </div>
            </Form.Field>
            <Form.Field style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Icon name="university" size="large" color="teal" style={{ flexShrink: 0 }} />
                <Radio
                  label="SEPA-Lastschrift / Bankkonto (Stripe)"
                  name="subscription_pay"
                  value="stripe_sepa"
                  checked={paymentMethod === "stripe_sepa"}
                  onChange={(_e, { value }) => {
                    setPaymentMethod(value);
                    setError(null);
                  }}
                />
              </div>
            </Form.Field>
            {paypalAvailable && (
              <Form.Field style={{ marginBottom: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Icon name="paypal" size="large" style={{ flexShrink: 0, color: "#0070ba" }} />
                  <Radio
                    label="PayPal"
                    name="subscription_pay"
                    value="paypal"
                    checked={paymentMethod === "paypal"}
                    onChange={(_e, { value }) => {
                      setPaymentMethod(value);
                      setError(null);
                    }}
                  />
                </div>
              </Form.Field>
            )}
          </Form>
        </div>

        {!paypalAvailable && paypalReason === "missing_plans" && (
          <Message warning compact style={{ marginBottom: "1.25rem" }}>
            PayPal ist derzeit nicht aktiv (Abrechnungspläne fehlen). Du kannst mit Karte oder SEPA-Lastschrift über Stripe zahlen.
          </Message>
        )}

        <Header as="h3" style={{ fontWeight: 600, fontSize: 17, color: "#1a1a1a", marginBottom: 16 }}>
          Tarif wählen
        </Header>

        <Card.Group itemsPerRow={3} stackable doubling>
          {plans.map((p) => (
            <Card
              key={p.key}
              style={{
                borderRadius: 12,
                overflow: "hidden",
                border: p.recommended ? "2px solid #00b5ad" : "1px solid rgba(0,0,0,0.1)",
                boxShadow: p.recommended
                  ? "0 4px 20px rgba(0, 181, 173, 0.15)"
                  : "0 1px 4px rgba(0,0,0,0.06)",
                background: "#fff",
              }}
            >
              {p.recommended && (
                <Label color="teal" ribbon style={{ zIndex: 1 }}>
                  Beliebt
                </Label>
              )}
              <Card.Content style={{ paddingTop: p.recommended ? "2rem" : "1.25rem" }}>
                <Card.Header style={{ fontSize: 18, fontWeight: 600, color: "#1a1a1a" }}>
                  {p.label}
                </Card.Header>
                <Card.Meta style={{ marginTop: 12 }}>
                  <span style={{ fontSize: 28, fontWeight: 700, color: "#1a1a1a" }}>
                    {p.price.toFixed(0)}
                    {" "}
                    €
                  </span>
                  <span style={{ color: "#666", fontSize: 15 }}>
                    {" "}
                    / {p.months}
                    {" "}
                    {p.months === 1 ? "Monat" : "Monate"}
                  </span>
                </Card.Meta>
                <Card.Description style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 14, color: "#555", marginBottom: 10 }}>
                    <Icon name="calendar alternate outline" color="teal" />
                    {" "}
                    <strong>{p.perMonth.toFixed(2)} €</strong>
                    {" "}
                    pro Monat (effektiv)
                  </div>
                  <List relaxed size="small" style={{ color: "#444" }}>
                    {p.features.map((f, idx) => (
                      <List.Item key={idx} icon="check" content={f} />
                    ))}
                  </List>
                </Card.Description>
              </Card.Content>
              <Card.Content
                extra
                style={{
                  background: "#fafafa",
                  borderTop: "1px solid rgba(0,0,0,0.06)",
                  padding: "1rem 1.15rem",
                }}
              >
                {(paymentMethod === "stripe_card" || paymentMethod === "stripe_sepa") && (
                  <Button
                    primary
                    fluid
                    className="cuddle"
                    icon
                    labelPosition="right"
                    loading={loadingKey || loadingPlan === p.key}
                    disabled={!stripe}
                    onClick={() => startCheckout(p.key)}
                  >
                    {paymentMethod === "stripe_sepa"
                      ? "Weiter zur Lastschrift"
                      : "Weiter zur Kasse (Karte)"}
                    <Icon name="arrow right" />
                  </Button>
                )}
                {/*
                  Keep PayPal mount nodes always in the DOM when PayPal is offered.
                  Conditionally mounting/unmounting them causes React removeChild errors
                  after PayPal injects iframes (switching Stripe ↔ PayPal).
                */}
                {paypalAvailable && (
                  <div
                    id={`paypal-btn-${p.key}`}
                    style={{
                      minHeight: 48,
                      display: paymentMethod === "paypal" ? "block" : "none",
                    }}
                    aria-hidden={paymentMethod !== "paypal"}
                  />
                )}
              </Card.Content>
            </Card>
          ))}
        </Card.Group>

        <Divider style={{ margin: "1.75rem 0 1.25rem" }} />

        <Message info icon style={{ borderRadius: 8, marginBottom: 0 }}>
          <Icon name="info circle" />
          <Message.Content>
            <Message.Header style={{ marginBottom: 6 }}>Hinweis zur gewählten Zahlungsart</Message.Header>
            {paymentMethod === "stripe_card" && (
              <p style={{ margin: 0, lineHeight: 1.55, color: "#444" }}>
                Du wirst zu Stripe Checkout weitergeleitet. Nach Abschluss hast du in der Regel sofort Chat‑Zugang. Kündigung jederzeit im Stripe-Kundenportal.
              </p>
            )}
            {paymentMethod === "stripe_sepa" && (
              <p style={{ margin: 0, lineHeight: 1.55, color: "#444" }}>
                SEPA-Lastschrift: Mandat und IBAN bei Stripe. Die erste Abbuchung kann einige Werktage dauern; Zugang, sobald Stripe das Abo als aktiv meldet. Verwaltung im Stripe-Kundenportal.
              </p>
            )}
            {paymentMethod === "paypal" && (
              <p style={{ margin: 0, lineHeight: 1.55, color: "#444" }}>
                Mit PayPal abonnieren: Button nutzen, bei PayPal bestätigen — danach Chat‑Zugang. Abo unter PayPal → Zahlungen / Abonnements verwalten.
              </p>
            )}
          </Message.Content>
        </Message>
      </div>
    </div>
  );
}
